import axios from 'axios';

const ADMIN_URL = process.env.VUE_APP_ADMIN_URL;

function authHeaders() {
  const token = JSON.parse(localStorage.getItem('accessToken'));
  return { Authorization: `Bearer ${token}` };
}

function jsonConfig() {
  return { headers: { ...authHeaders(), 'Content-Type': 'application/json' } };
}

// Multipart: el Content-Type lo pone axios para incluir el boundary.
function uploadConfig() {
  return { headers: authHeaders() };
}

// El backend responde { error: { code, message } }; se acepta también { code, message } plano.
function toError(error) {
  const body = (error.response && error.response.data) || {};
  const detail = body.error || body;
  return {
    status: 'error',
    code: detail.code,
    message: detail.message ?? error.message,
    httpCode: error.response && error.response.status
  };
}

function settlementUrl(clinicId, cutoffDate) {
  return `${ADMIN_URL}/clinic-settlements/${clinicId}/${cutoffDate}`;
}

function toFormData(fields) {
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') formData.append(key, value);
  });
  return formData;
}

/**
 * "Facturación de Clínicas" (pawer-backend specs/125-clinic-billing-admin,
 * contracts/admin-clinic-settlements.md). Same contract as the rest of
 * src/services/: { status: 'ok', data, meta? } on success,
 * { status: 'error', code, message, httpCode } on error.
 */
export default class ClinicSettlementsService {
  /**
   * Server-side page of settlements. POST and not GET: the search can be a RUT
   * or an invoice number, which must not travel in the URL.
   * @param {{page: number, pageSize: number, search?: string, clinicId?: string|null, statuses?: string[]}} filters
   */
  static async search({ page, pageSize, search, clinicId, statuses }) {
    try {
      const response = await axios.post(
        `${ADMIN_URL}/clinic-settlements/search`,
        { page, pageSize, search: search || undefined, clinicId: clinicId || null, statuses: statuses || [] },
        jsonConfig()
      );
      return { status: 'ok', data: response.data.data, meta: response.data.meta };
    } catch (error) {
      return toError(error);
    }
  }

  /** Detail of one settlement (clinic × cutoff), with attendances and history. */
  static async getDetail(clinicId, cutoffDate) {
    try {
      const response = await axios.get(settlementUrl(clinicId, cutoffDate), jsonConfig());
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return toError(error);
    }
  }

  /**
   * Registers or corrects the clinic's invoice. Returns the refreshed detail.
   * @param {{invoiceNumber: string, invoiceIssuedAt: string, pdf?: File, xml?: File}} invoice
   */
  static async registerInvoice(clinicId, cutoffDate, { invoiceNumber, invoiceIssuedAt, pdf, xml }) {
    try {
      const response = await axios.put(
        `${settlementUrl(clinicId, cutoffDate)}/invoice`,
        toFormData({ invoiceNumber, invoiceIssuedAt, pdf, xml }),
        uploadConfig()
      );
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return toError(error);
    }
  }

  /**
   * Short-lived signed URL of a document.
   * @param {'invoice-pdf'|'invoice-xml'|'receipt'} kind
   */
  static async getDocumentUrl(clinicId, cutoffDate, kind) {
    try {
      const response = await axios.get(`${settlementUrl(clinicId, cutoffDate)}/documents/${kind}`, jsonConfig());
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return toError(error);
    }
  }

  /** Manual status change; `reason` only for `canceled`. Returns the refreshed detail. */
  static async changeStatus(clinicId, cutoffDate, { status, reason, creditNoteAmount }) {
    try {
      const body = { status };
      if ((status === 'canceled' || status === 'credit_note_required') && reason) body.reason = reason;
      if (status === 'credit_note_required' && creditNoteAmount) body.creditNoteAmount = creditNoteAmount;
      const response = await axios.patch(`${settlementUrl(clinicId, cutoffDate)}/status`, body, jsonConfig());
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return toError(error);
    }
  }

  /** Cancels one attendance of the settlement. Returns the refreshed detail. */
  static async cancelAttendance(clinicId, cutoffDate, invoiceId, reason) {
    try {
      const response = await axios.post(
        `${settlementUrl(clinicId, cutoffDate)}/attendances/${invoiceId}/cancel`,
        { reason },
        jsonConfig()
      );
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return toError(error);
    }
  }

  /** Undoes the cancellation of one attendance. Returns the refreshed detail. */
  static async restoreAttendance(clinicId, cutoffDate, invoiceId) {
    try {
      const response = await axios.post(
        `${settlementUrl(clinicId, cutoffDate)}/attendances/${invoiceId}/restore`,
        {},
        jsonConfig()
      );
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return toError(error);
    }
  }

  /** Uploads the transfer receipt of a paid settlement. Returns the refreshed detail. */
  static async uploadReceipt(clinicId, cutoffDate, file) {
    try {
      const response = await axios.put(
        `${settlementUrl(clinicId, cutoffDate)}/receipt`,
        toFormData({ receipt: file }),
        uploadConfig()
      );
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return toError(error);
    }
  }

  /**
   * Applies the rows of a payments file (each row on its own).
   * @param {Array<{rowNumber: number, clinicRut: string, invoiceNumber: string, status: string, paymentDate: string}>} rows
   * @returns {Promise<{status: 'ok', data: {applied: number, rejected: Array<{rowNumber: number, reason: string}>}}|object>}
   */
  static async applyPayments(rows) {
    try {
      const response = await axios.post(`${ADMIN_URL}/clinic-settlements/payments`, { rows }, jsonConfig());
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return toError(error);
    }
  }

  /** Transfer payroll of the settlements in `pending_payment`: { filename, content, count, excluded }. */
  static async getPayroll() {
    try {
      const response = await axios.get(`${ADMIN_URL}/clinic-settlements/payroll`, jsonConfig());
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return toError(error);
    }
  }
}
